import * as React from 'react';
import { ChakraProvider, CSSReset } from '@chakra-ui/react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import moment from 'moment';
import { theme } from './theme';

// locales
import './constants/locales/i18n';
import 'moment/locale/pt-br';

import AppRouter from './routes';
import { AuthProvider } from './providers/auth';
import { queryClient } from './services/queryConfig';
import { DialogProvider } from './providers/dialog';
import { ExclusaoPendenteProvider } from './providers/exclusaoPendente';
import { SolicitacaoAcessoProvider } from './providers/solicitacaoAcesso';
import { useTabCloseDetection } from './hooks/useTabCloseDetection';
// eslint-disable-next-line import/extensions
import { SolicitacaoAcessoModalGlobal } from './components/SolicitacaoAcessoModal/SolicitacaoAcessoModalGlobal';
import { NotificationProvider } from './providers/notification';
import { NotificationBell } from './components/NotificationBell';
import { NotificationPanel } from './components/NotificationPanel';
import { WhatsAppNotificationProvider } from './providers/whatsappNotification';

// Configurar moment.js para usar locale pt-br globalmente
moment.locale('pt-br');

function AppContent() {
  useTabCloseDetection();

  return (
    <>
      <AppRouter />
      <SolicitacaoAcessoModalGlobal />
      <NotificationBell />
      <NotificationPanel />
    </>
  );
}

// Limpa qualquer valor 'dark' herdado do toggle antigo (removido). So sobrescreve
// se o valor atual for 'dark' — preserva 'light' explicito do user e nao toca em
// outros valores futuros que o Chakra possa usar.
// Roda 1x na carga do modulo, fora do render, pra evitar side-effect em StrictMode.
if (typeof window !== 'undefined') {
  try {
    if (window.localStorage.getItem('chakra-ui-color-mode') === 'dark') {
      window.localStorage.setItem('chakra-ui-color-mode', 'light');
    }
  } catch {
    // ignore
  }
}

export function App() {
  return (
    <ChakraProvider
      theme={theme}
      // Sessao 2026-05-11 (item 11 Fase 1): default global pros toasts —
      // top-right evita cobrir CTAs centralizados (ex: botao Continuar do
      // wizard de criar contrato societario). Toasts individuais ainda
      // podem sobrescrever via useToast({ position: '...' }).
      toastOptions={{ defaultOptions: { position: 'top-right' } }}
    >
      <CSSReset />
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          <AuthProvider>
            <NotificationProvider>
              <SolicitacaoAcessoProvider>
                <DialogProvider>
                  {/* `visibleToasts` baixo + `closeButton`: os avisos ficam no mesmo canto
    do sino de notificacoes, e uma pilha deles cobria o botao. */}
                  <Toaster
                    richColors
                    position="top-right"
                    visibleToasts={2}
                    closeButton
                    toastOptions={{ duration: 6000 }}
                  />
                  <ExclusaoPendenteProvider>
                    <WhatsAppNotificationProvider>
                      <AppContent />
                    </WhatsAppNotificationProvider>
                  </ExclusaoPendenteProvider>
                </DialogProvider>
              </SolicitacaoAcessoProvider>
            </NotificationProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ChakraProvider>
  );
}
